import { db } from '../config/firebase';
import { collection, doc, addDoc, getDocs, deleteDoc, query, where, updateDoc, writeBatch } from 'firebase/firestore';
import { API_BASE_URL } from '../config/constants';

class PractitionerService {
  constructor() {
            this.baseUrl = `${API_BASE_URL}/api`;
    this.collectionName = 'practitioners';
    this.appointmentsCollection = 'appointments';
  }

  /**
   * Search practitioners using FastAPI (Epic FHIR)
   * Backend only returns data - no saving
   */
  async searchPractitioners(name = '', specialty = '', location = '', limit = 20) {
    const params = new URLSearchParams();
    
    if (name) params.append('name', name);
    if (specialty) params.append('specialty', specialty);
    if (location) params.append('location', location);
    params.append('limit', limit.toString());

    try {
      const response = await fetch(`${this.baseUrl}/practitioners/search?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fhirResult = await response.json();
      
      // Transform FHIR data to our format and add appointment counts
      if (fhirResult.entry && fhirResult.entry.length > 0) {
        const practitioners = fhirResult.entry.map(entry => this.formatFhirPractitioner(entry.resource));
        
        // Get appointment counts and organization data for each practitioner
        const practitionersWithCounts = await Promise.all(
          practitioners.map(async (practitioner) => {
            try {
              const [appointmentCount, organizationData] = await Promise.all([
                this.getAppointmentCount(practitioner.fhir_id),
                this.getOrganizationData(practitioner.fhir_id)
              ]);
              
              return {
                ...practitioner,
                appointment_count: appointmentCount,
                has_appointments: appointmentCount > 0,
                hospital: organizationData.organization || 'Unknown',
                location: organizationData.location
              };
            } catch (error) {
              console.warn(`Failed to get data for ${practitioner.full_name}:`, error);
              return {
                ...practitioner,
                appointment_count: 0,
                has_appointments: false,
                hospital: 'Unknown'
              };
            }
          })
        );
        
        return practitionersWithCounts;
      }
      
      return [];
    } catch (error) {
      console.error('Error searching practitioners:', error);
      throw error;
    }
  }

  /**
   * Get appointment count for a practitioner (for search results)
   */
  async getAppointmentCount(practitionerId) {
    try {
      const params = new URLSearchParams({
        practitioner_id: practitionerId,
        force_practitioner_only: 'true',
        // limit: '100' // Get more to get accurate count
      });

      const response = await fetch(`${this.baseUrl}/appointments/search?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return 0;
      }

      const data = await response.json();
      return data.entry ? data.entry.length : 0;
    } catch (error) {
      console.warn('Error getting appointment count:', error);
      return 0;
    }
  }

  /**
   * Get organization data for a practitioner from PractitionerRole
   */
  async getOrganizationData(practitionerId) {
    try {
      const params = new URLSearchParams({
        practitioner_id: practitionerId,
        limit: '1'
      });

      const response = await fetch(`${this.baseUrl}/practitioner-roles/search?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return { organization: null, location: null };
      }

      const data = await response.json();
      if (data.entry && data.entry.length > 0) {
        const role = data.entry[0].resource;
        return {
          organization: role.organization?.display || role.organization?.reference || null,
          location: role.location?.[0]?.display || null
        };
      }
      
      return { organization: null, location: null };
    } catch (error) {
      console.warn('Error getting organization data:', error);
      return { organization: null, location: null };
    }
  }

  /**
   * Save practitioner to Firebase Firestore WITH their appointments
   */
  async savePractitioner(practitioner, userId) {
    try {
      // Check if already exists (by NPI or FHIR ID)
      const existingByNpi = await this.findPractitionerByNPI(practitioner.npi, userId);
      const existingByFhir = await this.findPractitionerByFhirId(practitioner.fhir_id, userId);
      if (existingByNpi || existingByFhir) {
        throw new Error('Practitioner already exists in your profile');
      }

      // Add user ID and timestamp
      const practitionerData = {
        ...practitioner,
        userId: userId,
        createdAt: new Date().toISOString(),
        source: 'epic_fhir'
      };

      // Save practitioner to Firestore
      const docRef = await addDoc(collection(db, this.collectionName), practitionerData);
      const practitionerId = docRef.id;
      
      // 🚀 NEW: Fetch and save appointments for this practitioner
      console.log(`🔄 Fetching appointments for ${practitioner.full_name}...`);
      
      try {
        const appointments = await this.fetchPractitionerAppointments(practitioner.fhir_id);
        
        if (appointments && appointments.length > 0) {
          console.log(`💾 Saving ${appointments.length} appointments for ${practitioner.full_name}`);
          await this.saveAppointmentsToFirebase(appointments, userId, practitionerId, practitioner);
        }
        
        return {
          success: true,
          id: practitionerId,
          message: `Practitioner and ${appointments?.length || 0} appointments added to your profile successfully`,
          appointmentCount: appointments?.length || 0
        };
      } catch (appointmentError) {
        console.warn('Failed to fetch appointments, but practitioner was saved:', appointmentError);
        return {
          success: true,
          id: practitionerId,
          message: 'Practitioner saved successfully (appointments could not be loaded)',
          appointmentCount: 0
        };
      }
      
    } catch (error) {
      console.error('Error saving practitioner:', error);
      return {
        success: false,
        message: error.message || 'Failed to save practitioner'
      };
    }
  }

  /**
   * Get saved practitioners for a user
   */
  async getSavedPractitioners(userId) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const practitioners = [];
      
      querySnapshot.forEach((doc) => {
        practitioners.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return practitioners;
    } catch (error) {
      console.error('Error getting saved practitioners:', error);
      return [];
    }
  }

  /**
   * Remove practitioner AND their appointments from Firebase
   */
  async removePractitioner(practitionerId) {
    try {
      // First, remove all appointments for this practitioner
      console.log(`🗑️ Removing appointments for practitioner ${practitionerId}...`);
      await this.removeAppointmentsForPractitioner(practitionerId);
      
      // Then remove the practitioner
      await deleteDoc(doc(db, this.collectionName, practitionerId));
      
      return {
        success: true,
        message: 'Practitioner and all associated appointments removed from your profile'
      };
    } catch (error) {
      console.error('Error removing practitioner:', error);
      return {
        success: false,
        message: 'Failed to remove practitioner'
      };
    }
  }

  /**
   * Remove all appointments for a specific practitioner
   */
  async removeAppointmentsForPractitioner(practitionerId) {
    try {
      const q = query(
        collection(db, this.appointmentsCollection),
        where('practitionerId', '==', practitionerId)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      console.log(`✅ Removed ${querySnapshot.size} appointments for practitioner ${practitionerId}`);
    } catch (error) {
      console.error('Error removing appointments for practitioner:', error);
      throw error; // Re-throw to be handled by removePractitioner
    }
  }

  /**
   * Find practitioner by NPI for duplicate checking
   */
  async findPractitionerByNPI(npi, userId) {
    try {
      if (!npi) return null;
      
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('npi', '==', npi)
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty ? querySnapshot.docs[0].data() : null;
    } catch (error) {
      console.error('Error finding practitioner by NPI:', error);
      return null;
    }
  }

  /**
   * Find practitioner by FHIR ID for duplicate checking when NPI missing
   */
  async findPractitionerByFhirId(fhirId, userId) {
    try {
      if (!fhirId) return null;
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('fhir_id', '==', fhirId)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty ? querySnapshot.docs[0].data() : null;
    } catch (error) {
      console.error('Error finding practitioner by FHIR ID:', error);
      return null;
    }
  }

  /**
   * Format FHIR Practitioner data to our simplified format
   */
  formatFhirPractitioner(fhirPractitioner) {
    const name = fhirPractitioner.name?.[0] || {};
    const telecom = fhirPractitioner.telecom || [];
    const address = fhirPractitioner.address?.[0] || {};
    const qualification = fhirPractitioner.qualification?.[0] || {};

    // Extract contact info
    const phone = telecom.find(t => t.system === 'phone')?.value || '';
    const email = telecom.find(t => t.system === 'email')?.value || '';

    // Extract specialty
    const specialty = qualification.code?.coding?.[0]?.display || 'General Practice';

    // Extract NPI
    const npi = fhirPractitioner.identifier?.find(i => 
      i.system === 'http://hl7.org/fhir/sid/us-npi'
    )?.value || '';

    return {
      fhir_id: fhirPractitioner.id,
      npi: npi,
      prefix: name.prefix?.join(' ') || 'Dr.',
      first_name: name.given?.join(' ') || '',
      last_name: name.family || '',
      full_name: `${name.given?.join(' ') || ''} ${name.family || ''}`.trim(),
      // Enhanced data from Epic FHIR
      gender: fhirPractitioner.gender || 'Unknown',
      active: fhirPractitioner.active || false,
      languages: fhirPractitioner.communication?.map(c => c.text || c.coding?.[0]?.display).filter(Boolean) || ['English'],
      identifiers: fhirPractitioner.identifier || [],
      full_epic_data: fhirPractitioner, // Save complete FHIR data
      phone: phone,
      email: email,
      specialty: specialty,
      hospital: null, // Will be fetched from PractitionerRole
      address: {
        line: address.line || [],
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postalCode || ''
      },
      active: fhirPractitioner.active !== false
    };
  }

  /**
   * Fetch appointments for a practitioner from the backend API
   */
  async fetchPractitionerAppointments(practitionerId) {
    try {
      const currentYear = new Date().getFullYear();
      const params = new URLSearchParams({
        practitioner_id: practitionerId,
        force_practitioner_only: 'true',
        limit: '50'
      });

      const response = await fetch(`${this.baseUrl}/appointments/search?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fhirResult = await response.json();
      
      // Transform FHIR appointments to our format
      if (fhirResult.entry && fhirResult.entry.length > 0) {
        return fhirResult.entry.map(entry => this.formatFhirAppointment(entry.resource));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching practitioner appointments:', error);
      throw error;
    }
  }

  /**
   * Save appointments to Firebase with user and practitioner links
   */
  async saveAppointmentsToFirebase(appointments, userId, practitionerId, practitioner) {
    try {
      const batch = writeBatch(db);
      const appointmentsRef = collection(db, this.appointmentsCollection);

      appointments.forEach(appointment => {
        const appointmentData = {
          ...appointment,
          userId: userId,
          practitionerId: practitionerId,
          practitionerName: practitioner.full_name,
          practitionerNPI: practitioner.npi,
          practitionerSpecialty: practitioner.specialty,
          createdAt: new Date().toISOString(),
          source: 'epic_fhir'
        };

        const docRef = doc(appointmentsRef);
        batch.set(docRef, appointmentData);
      });

      await batch.commit();
      console.log(`✅ Successfully saved ${appointments.length} appointments to Firebase`);
    } catch (error) {
      console.error('Error saving appointments to Firebase:', error);
      throw error;
    }
  }

  /**
   * Get saved appointments for a user's practitioners
   */
  async getSavedAppointments(userId, practitionerId = null) {
    try {
      let q;
      if (practitionerId) {
        q = query(
          collection(db, this.appointmentsCollection),
          where('userId', '==', userId),
          where('practitionerId', '==', practitionerId)
        );
      } else {
        q = query(
          collection(db, this.appointmentsCollection),
          where('userId', '==', userId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const appointments = [];
      
      querySnapshot.forEach((doc) => {
        appointments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by start date
      appointments.sort((a, b) => new Date(a.start) - new Date(b.start));
      
      return appointments;
    } catch (error) {
      console.error('Error getting saved appointments:', error);
      return [];
    }
  }

  /**
   * Transform FHIR appointment to our format
   */
  formatFhirAppointment(fhirAppointment) {
    const participant = fhirAppointment.participant?.[0] || {};
    const reasonCode = fhirAppointment.reasonCode?.[0]?.coding?.[0] || {};

    // Extract facility name from FHIR location object
    let facilityName = 'Unknown'; // Default when no facility info available
    if (fhirAppointment.location) {
      if (typeof fhirAppointment.location === 'string') {
        facilityName = fhirAppointment.location;
      } else if (fhirAppointment.location.display) {
        facilityName = fhirAppointment.location.display;
      } else if (fhirAppointment.location.name) {
        facilityName = fhirAppointment.location.name;
      }
    }

    return {
      fhir_id: fhirAppointment.id,
      status: fhirAppointment.status,
      description: fhirAppointment.description || 'Unknown Description',
      start: fhirAppointment.start,
      end: fhirAppointment.end,
      duration: this.calculateDuration(fhirAppointment.start, fhirAppointment.end),
      procedure: reasonCode.display || fhirAppointment.description || 'Unknown Procedure',
      snomedCode: reasonCode.code || '',
      practitionerReference: participant.actor?.reference || '',
      location: facilityName,
      priority: fhirAppointment.priority || 'routine'
    };
  }

  /**
   * Calculate appointment duration in minutes
   */
  calculateDuration(start, end) {
    if (!start || !end) return 60; // Default 1 hour
    
    const startTime = new Date(start);
    const endTime = new Date(end);
    return Math.round((endTime - startTime) / (1000 * 60)); // Minutes
  }

  /**
   * Refresh appointments for all saved practitioners
   */
  async refreshAllAppointments(userId) {
    try {
      console.log('🔄 Refreshing appointments for all saved practitioners...');
      
      // Get all saved practitioners
      const savedPractitioners = await this.getSavedPractitioners(userId);
      
      if (!savedPractitioners || savedPractitioners.length === 0) {
        return {
          success: true,
          message: 'No practitioners to refresh',
          refreshCount: 0
        };
      }

      let refreshCount = 0;
      const errors = [];

      // Refresh appointments for each practitioner
      for (const practitioner of savedPractitioners) {
        try {
          console.log(`🔄 Refreshing appointments for ${practitioner.full_name}...`);
          
          // Remove old appointments
          await this.removeAppointmentsForPractitioner(practitioner.id);
          
          // Fetch fresh appointments
          const appointments = await this.fetchPractitionerAppointments(practitioner.fhir_id);
          
          if (appointments && appointments.length > 0) {
            // Save fresh appointments
            await this.saveAppointmentsToFirebase(appointments, userId, practitioner.id, practitioner);
            refreshCount++;
            console.log(`✅ Refreshed ${appointments.length} appointments for ${practitioner.full_name}`);
          }
        } catch (error) {
          console.error(`❌ Failed to refresh appointments for ${practitioner.full_name}:`, error);
          errors.push(`${practitioner.full_name}: ${error.message}`);
        }
      }

      return {
        success: true,
        message: `Successfully refreshed appointments for ${refreshCount} of ${savedPractitioners.length} practitioners`,
        refreshCount,
        errors: errors.length > 0 ? errors : null
      };
    } catch (error) {
      console.error('Error refreshing appointments:', error);
      return {
        success: false,
        message: 'Failed to refresh appointments',
        error: error.message
      };
    }
  }

  /**
   * 🚀 MIGRATION: Backfill appointments for existing practitioners
   * Run this ONCE to migrate old practitioners to the new system
   */
  async migrateExistingPractitioners(userId) {
    try {
      console.log('🔄 MIGRATION: Backfilling appointments for existing practitioners...');
      
      // Get all saved practitioners
      const savedPractitioners = await this.getSavedPractitioners(userId);
      
      if (!savedPractitioners || savedPractitioners.length === 0) {
        return {
          success: true,
          message: 'No existing practitioners to migrate',
          migratedCount: 0
        };
      }

      // Check if appointments already exist
      const existingAppointments = await this.getSavedAppointments(userId);
      if (existingAppointments && existingAppointments.length > 0) {
        return {
          success: true,
          message: `Migration not needed - ${existingAppointments.length} appointments already exist`,
          migratedCount: 0
        };
      }

      console.log(`📋 Found ${savedPractitioners.length} existing practitioners to migrate`);
      let migratedCount = 0;
      const errors = [];

      // Migrate each practitioner
      for (const practitioner of savedPractitioners) {
        try {
          console.log(`🔄 Migrating appointments for ${practitioner.full_name}...`);
          
          // Fetch appointments from backend
          const appointments = await this.fetchPractitionerAppointments(
            practitioner.npi || practitioner.fhir_id
          );
          
          if (appointments && appointments.length > 0) {
            // Save appointments to Firebase
            await this.saveAppointmentsToFirebase(appointments, userId, practitioner.id, practitioner);
            migratedCount++;
            console.log(`✅ Migrated ${appointments.length} appointments for ${practitioner.full_name}`);
          } else {
            console.log(`⚠️ No appointments found for ${practitioner.full_name}`);
          }
        } catch (error) {
          console.error(`❌ Failed to migrate ${practitioner.full_name}:`, error);
          errors.push(`${practitioner.full_name}: ${error.message}`);
        }
      }

      return {
        success: true,
        message: `Migration complete! Migrated appointments for ${migratedCount} of ${savedPractitioners.length} practitioners`,
        migratedCount,
        totalPractitioners: savedPractitioners.length,
        errors: errors.length > 0 ? errors : null
      };
    } catch (error) {
      console.error('Error during migration:', error);
      return {
        success: false,
        message: 'Migration failed',
        error: error.message
      };
    }
  }

  /**
   * Migrate existing Firebase appointments to fix FHIR object issues
   * This converts location/hospital objects to strings to prevent React rendering errors
   */
  async migrateFirebaseAppointmentData(userId) {
    try {
      console.log('🔄 Starting appointment data migration...');
      
      const q = query(
        collection(db, this.appointmentsCollection),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      let migrationCount = 0;
      
      querySnapshot.forEach((doc) => {
        const appointment = doc.data();
        let needsUpdate = false;
        const updates = {};
        
        // Fix location field if it's an object
        if (appointment.location && typeof appointment.location === 'object') {
          if (appointment.location.display) {
            updates.location = appointment.location.display;
            needsUpdate = true;
          } else if (appointment.location.name) {
            updates.location = appointment.location.name;
            needsUpdate = true;
          }
        }
        
        // Fix hospital field if it's an object
        if (appointment.hospital && typeof appointment.hospital === 'object') {
          if (appointment.hospital.display) {
            updates.hospital = appointment.hospital.display;
            needsUpdate = true;
          } else if (appointment.hospital.name) {
            updates.hospital = appointment.hospital.name;
            needsUpdate = true;
          }
        }
        
        if (needsUpdate) {
          batch.update(doc.ref, updates);
          migrationCount++;
        }
      });
      
      if (migrationCount > 0) {
        await batch.commit();
        console.log(`✅ Successfully migrated ${migrationCount} appointments`);
        return {
          success: true,
          migratedCount: migrationCount,
          message: `Fixed facility data for ${migrationCount} appointments`
        };
      } else {
        console.log('✅ No appointments needed migration');
        return {
          success: true,
          migratedCount: 0,
          message: 'All appointment data is already clean'
        };
      }
    } catch (error) {
      console.error('❌ Appointment data migration failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default PractitionerService; 